/**
 * A raw device reading in, one frame of pilot intent out. Pure: a plain
 * `PadSnapshot` and a `GamepadMapping`, no DOM, no clock, no module state — so
 * every awkward stick in the world is an ordinary unit test rather than
 * something you find out about in flight.
 *
 * ## The transform, in the order it has to happen
 *
 * 1. **Calibrate.** `(raw − centre) / travel` puts the device's own rest
 *    position at zero and its own limits at ±1, which is the whole reason
 *    calibration exists: a stick whose centre reads 0.04 and whose right limit
 *    reads 0.86 must still give full aileron at full deflection and none at
 *    rest.
 * 2. **Sense.** `invert` flips it, because half the throttles in the world run
 *    the other way and no amount of arithmetic can tell which half.
 * 3. **Dead zone.** Anything inside reads exactly zero, and the remainder is
 *    *rescaled* so the first millimetre outside the zone is a small input
 *    rather than a jump to the dead zone's own width. Rescaling is the half
 *    that people forget and it is the half you feel.
 * 4. **Curve.** `sign(v)·|v|^k`, which softens the centre without moving the
 *    limits — full deflection is full deflection on every curve, so choosing
 *    one never costs authority.
 *
 * The throttle takes 1–3 and **not** 4. A response curve exists to make small
 * stick corrections finer near neutral; a throttle has no neutral, and bending
 * its lever would only make the gate lie about where half power is. Its dead
 * zone is applied at *both* ends instead, which is what gives a quadrant its
 * idle and full detents.
 */

import type { GlanceDirection } from "./keys.ts";
import {
  HELD_ACTIONS,
  PAD_ACTIONS,
  UNMAPPED,
  type AxisMapping,
  type GamepadMapping,
  type HatSource,
  type PadAction,
  type ResponseCurve,
} from "./gamepad-mapping.ts";

/**
 * One poll of one device, in the shape the pure layer reads. `gamepad.ts` is
 * the only place a real `Gamepad` is turned into this.
 */
export interface PadSnapshot {
  readonly id: string;
  /** The browser's `Gamepad.mapping === "standard"` promise about axis and button order. */
  readonly standard: boolean;
  readonly axes: readonly number[];
  readonly buttons: readonly boolean[];
}

/** Each role's reading, or `undefined` where this device has no axis for it. */
export interface PadAxes {
  readonly pitch: number | undefined;
  readonly roll: number | undefined;
  readonly yaw: number | undefined;
  readonly throttle: number | undefined;
}

export const NO_PAD_AXES: PadAxes = { pitch: undefined, roll: undefined, yaw: undefined, throttle: undefined };

export interface PadFrame {
  /** The connected device's id, or `undefined` when there is no stick — which is also what a *blocked* frame reports. */
  readonly device: string | undefined;
  readonly axes: PadAxes;
  /** Level-triggered actions (`HELD_ACTIONS`), read exactly as a held key is. */
  readonly held: ReadonlySet<PadAction>;
  /** Rising edges of the one-shot actions only, so a consumer cannot fire one twice. */
  readonly commands: readonly PadAction[];
  /** Where the hat is pointing, as a cockpit glance; `undefined` when it is centred or absent. */
  readonly glance: GlanceDirection | undefined;
}

export const NO_PAD_INPUT: PadFrame = {
  device: undefined,
  axes: NO_PAD_AXES,
  held: new Set<PadAction>(),
  commands: [],
  glance: undefined,
};

/**
 * How hard each curve bends. `fine` is deliberately a long way from `linear`:
 * a curve you cannot feel is a setting nobody will ever choose, and the whole
 * point of the option is gunnery, where the useful corrections are the ones in
 * the first tenth of travel.
 */
const CURVE_EXPONENTS: Readonly<Record<ResponseCurve, number>> = { linear: 1, smooth: 1.5, fine: 2.2 };

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Calibrated and sense-corrected, before the dead zone. Not clamped — the caller's range differs by role. */
function calibrated(raw: number, mapping: AxisMapping): number {
  if (!Number.isFinite(raw)) return 0;
  const scaled = (raw - mapping.centre) / mapping.travel;
  return mapping.invert ? -scaled : scaled;
}

/**
 * A centred axis's dead zone: zero inside, and outside it the remaining travel
 * is stretched back over the full 0…1 so the reading is continuous at the edge.
 */
export function applyDeadZone(value: number, deadZone: number): number {
  const size = Math.abs(value);
  if (size <= deadZone) return 0;
  const span = 1 - deadZone;
  if (span <= 0) return 0;
  return Math.sign(value) * Math.min((size - deadZone) / span, 1);
}

/** `sign(v)·|v|^k` — softens the centre, leaves ±1 exactly where it was. */
export function applyCurve(value: number, curve: ResponseCurve): number {
  const exponent = CURVE_EXPONENTS[curve];
  if (exponent === 1) return value;
  return Math.sign(value) * Math.abs(value) ** exponent;
}

/** One of the three control axes: calibrate, dead-zone, curve, clamp to ±1. */
export function centredAxisReading(raw: number, mapping: AxisMapping, deadZone: number, curve: ResponseCurve): number {
  return applyCurve(applyDeadZone(clamp(calibrated(raw, mapping), -1, 1), deadZone), curve);
}

/**
 * The throttle lever: 0 at the calibrated idle end, 1 at the calibrated open
 * end, with the dead zone spent at *both* ends as detents. No curve — see the
 * module header.
 */
export function throttleAxisReading(raw: number, mapping: AxisMapping, deadZone: number): number {
  const lever = clamp(calibrated(raw, mapping), 0, 1);
  const span = 1 - 2 * deadZone;
  if (span <= 0) return lever >= 0.5 ? 1 : 0;
  return clamp((lever - deadZone) / span, 0, 1);
}

/**
 * The DirectInput hat as an axis: eight detents evenly spaced over −1…+1
 * (`(dir · 2/7) − 1`, `dir` 0 = up and clockwise from there), and a centre that
 * lands outside that range — 3.2857 on the encoding every driver this port has
 * met uses.
 *
 * The **detent test is what makes this safe.** Rejecting only the out-of-range
 * centre would read a driver that reports a plain `0` for centred as
 * *down-and-a-half*, and a hat that thinks it is being held is a camera that
 * never looks forward again. So a value has to land within a fifth of a detent
 * of a real one to count, and `0` — which is 3.5 detents — does not.
 */
export function hatDirectionOf(value: number): number | undefined {
  if (!Number.isFinite(value) || Math.abs(value) > 1.05) return undefined;
  const detent = (value + 1) * 3.5;
  const nearest = Math.round(detent);
  if (Math.abs(detent - nearest) > 0.2 || nearest < 0 || nearest > 7) return undefined;
  return nearest;
}

/**
 * Hat detent → cockpit glance. The four diagonals fold to their **horizontal**
 * half, because "up and to the left" is a pilot looking left: the vertical
 * glances are the bomb-aiming and top-wing views (`keys.ts`'s `T`/`G`), which
 * are deliberate, and the horizontal ones are the ones a dogfight uses.
 */
const HAT_GLANCES: readonly GlanceDirection[] = ["up", "right", "right", "right", "down", "left", "left", "left"];

function hatGlance(snapshot: PadSnapshot, hat: HatSource): GlanceDirection | undefined {
  if (hat.kind === "axis") {
    const direction = hatDirectionOf(snapshot.axes[hat.index] ?? Number.NaN);
    return direction === undefined ? undefined : HAT_GLANCES[direction];
  }
  if (hat.kind !== "buttons") return undefined;
  // Same priority the keyboard's `glanceDirectionFor` uses for two keys at
  // once, minus the back glance, which is a button rather than a hat position.
  if (snapshot.buttons[hat.left]) return "left";
  if (snapshot.buttons[hat.right]) return "right";
  if (snapshot.buttons[hat.up]) return "up";
  if (snapshot.buttons[hat.down]) return "down";
  return undefined;
}

/** Which actions this device's buttons are asserting right now, held and one-shot alike. */
export function padActionsDown(snapshot: PadSnapshot, mapping: GamepadMapping): Set<PadAction> {
  const down = new Set<PadAction>();
  for (const action of PAD_ACTIONS) {
    const index = mapping.buttons[action];
    if (index !== UNMAPPED && snapshot.buttons[index] === true) down.add(action);
  }
  return down;
}

function axisFor(snapshot: PadSnapshot, mapping: GamepadMapping, role: "pitch" | "roll" | "yaw"): number | undefined {
  const axis = mapping.axes[role];
  if (axis.index === UNMAPPED) return undefined;
  return centredAxisReading(snapshot.axes[axis.index] ?? 0, axis, mapping.deadZone, mapping.curve);
}

/** Every mapped axis, read through the whole transform. */
export function padAxesFrom(snapshot: PadSnapshot, mapping: GamepadMapping): PadAxes {
  const throttle = mapping.axes.throttle;
  return {
    pitch: axisFor(snapshot, mapping, "pitch"),
    roll: axisFor(snapshot, mapping, "roll"),
    yaw: axisFor(snapshot, mapping, "yaw"),
    throttle:
      throttle.index === UNMAPPED
        ? undefined
        : throttleAxisReading(snapshot.axes[throttle.index] ?? 0, throttle, mapping.deadZone),
  };
}

/**
 * Actions still held over from a stretch when the device was being swallowed
 * by a panel, and which therefore must not act until they are **released**.
 *
 * This is the polled control's version of a rule the keyboard gets for free.
 * `PreferencesOverlay.tsx` swallows `keydown` on the capture phase, so a key
 * held when the panel opened is simply never in the `pressed` set, and closing
 * the panel with it still down does nothing until it is released and struck
 * again. A gamepad has no events to swallow — its buttons are a level, read
 * fresh every frame — so without this a trigger held through the panel would
 * start firing the instant the panel shut.
 *
 * The case that makes it matter is not hypothetical: the calibration flow asks
 * the player to confirm each step **with the stick's own buttons**, and the
 * last of those presses is a finger on the trigger at the moment the panel is
 * dismissed.
 *
 * Pass the previous suppression set and this frame's held set; anything the
 * player has let go of drops out, and the rest stays suppressed.
 */
export function stillSuppressed(suppressed: ReadonlySet<PadAction>, held: ReadonlySet<PadAction>): Set<PadAction> {
  const next = new Set<PadAction>();
  for (const action of suppressed) {
    if (held.has(action)) next.add(action);
  }
  return next;
}

/** The frame the sim sees, with the carried-over actions taken out of it. */
export function withoutSuppressed(frame: PadFrame, suppressed: ReadonlySet<PadAction>): PadFrame {
  if (suppressed.size === 0) return frame;
  const held = new Set<PadAction>();
  for (const action of frame.held) {
    if (!suppressed.has(action)) held.add(action);
  }
  // `commands` needs no filtering: a suppressed action was already down on the
  // previous frame, so it can have no rising edge to carry.
  return { ...frame, held };
}

/**
 * One frame of stick. `previousHeld` is the last frame's `held` set, and the
 * only state this layer needs: a one-shot action fires on the frame its button
 * goes down and never again while it stays down, which is the same rule
 * `keyboard.ts` gets for free from `KeyboardEvent.repeat`.
 */
export function padFrameFrom(
  snapshot: PadSnapshot,
  mapping: GamepadMapping,
  previousHeld: ReadonlySet<PadAction>,
): PadFrame {
  const down = padActionsDown(snapshot, mapping);
  const commands: PadAction[] = [];
  for (const action of down) {
    if (!HELD_ACTIONS.has(action) && !previousHeld.has(action)) commands.push(action);
  }
  return {
    device: snapshot.id,
    axes: padAxesFrom(snapshot, mapping),
    held: down,
    commands,
    glance: hatGlance(snapshot, mapping.hat),
  };
}
